import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../../components';
import { EnterViceService } from '../../../services';
import type { ImageParams, ProviderDescriptor, TestRun } from '../../../types/entervice';
import { getDateAndTime } from '../../../utils/dateMethods';
import StatusChip from './StatusChip';
import { describeError } from './errors';

export interface InlineSpec {
  provider: string;
  model: string;
  prompt: string;
  negativePrompt: string;
  params: ImageParams;
}

/** Video settings currently in the editor, sent with "Animate" (SCRUM-224). */
export interface InlineVideoSpec {
  provider: string;
  model: string;
  motionPrompt: string;
  durationSec: number;
  resolution: string;
}

interface Props {
  /** null while creating: tests run with the inline spec only and cannot be promoted to a thumbnail */
  presetId: string | null;
  providers: ProviderDescriptor[];
  /** current editor values; null when the form is not testable yet (no model / prompt / negative prompt) */
  readSpec: () => InlineSpec | null;
  /** `provider::model` currently selected in the editor, for the cost estimate */
  activeModelKey: string;
  /** current video settings; null when video is disabled or has no model yet */
  readVideoSpec: () => InlineVideoSpec | null;
  /** watched video fields so the Animate estimate follows the editor */
  videoForm: { enabled: boolean; modelKey: string; durationSec: number; resolution: string };
  /** last runs from the preset detail; merged into the local list on every refresh */
  initialRuns: TestRun[];
  canPromote: boolean;
  onRunFinished: (run: TestRun) => void;
  onThumbnailPromoted: (thumbnailUrl: string) => void;
}

const POLL_MS = 3000;
const MAX_BYTES = 6 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACTIVE_STATUSES = ['queued', 'processing'];

const isActive = (run: TestRun) => ACTIVE_STATUSES.indexOf(run.status) !== -1;

const mergeRuns = (server: TestRun[], local: TestRun[]): TestRun[] => {
  const byId: Record<string, TestRun> = {};
  server.forEach((run) => { byId[run.testId] = run; });
  local.forEach((run) => { if (!byId[run.testId]) byId[run.testId] = run; });
  return Object.keys(byId)
    .map((id) => byId[id])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

const formatCost = (cost: number | null | undefined) => {
  if (cost === null || cost === undefined) return null;
  return cost === 0 ? 'free' : `$${cost.toFixed(3)}`;
};

/**
 * Test Lab (SCRUM-219): upload a source image, run the current prompt through the provider,
 * poll the run every 3 s and promote a successful result to the preset thumbnail.
 * SCRUM-224: a finished image result can be animated with the editor's video settings; the video
 * run is polled the same way and plays inline.
 */
const TestLab: React.FC<Props> = ({
  presetId, providers, readSpec, activeModelKey, readVideoSpec, videoForm, initialRuns, canPromote, onRunFinished, onThumbnailPromoted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [useFormSpec, setUseFormSpec] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [runs, setRuns] = useState<TestRun[]>(initialRuns);
  const [starting, setStarting] = useState(false);
  const [animating, setAnimating] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promotedId, setPromotedId] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const finishedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    setRuns((prev) => mergeRuns(initialRuns, prev));
  }, [initialRuns]);

  // object URL housekeeping for the local preview
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  // WHY: one timer for every queued/processing run; re-armed after each poll while something is still active.
  useEffect(() => {
    const active = runs.filter(isActive);
    if (active.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      Promise.all(active.map((run) => EnterViceService.getTest(run.testId).catch(() => null)))
        .then((updates) => {
          const fresh = updates.filter((u): u is TestRun => u !== null);
          setRuns((prev) => prev.map((run) => fresh.find((u) => u.testId === run.testId) || run));
          fresh.forEach((run) => {
            if (!isActive(run) && !finishedRef.current[run.testId]) {
              finishedRef.current[run.testId] = true;
              onRunFinished(run);
            }
          });
          setTick((t) => t + 1);
        });
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [runs, onRunFinished]);

  const applyFile = useCallback((picked: File | null) => {
    setError('');
    if (!picked) return;
    if (ACCEPTED_TYPES.indexOf(picked.type) === -1) {
      setError('Use a JPG, PNG or WebP image.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError('Image is larger than 6 MB. Resize it and try again.');
      return;
    }
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
    setSourceUrl('');
  }, []);

  const clearSource = () => {
    setFile(null);
    setPreview(null);
  };

  const modelForEstimate = providers.find((p) => `${p.provider}::${p.model}` === activeModelKey);
  const estimate = modelForEstimate
    ? `≈ $${modelForEstimate.estCostUsd.toFixed(3)} per run`
    : null;

  // WHY: video is priced per output second, so the estimate follows the duration in the editor.
  const videoModel = videoForm.enabled ? providers.find((p) => `${p.provider}::${p.model}` === videoForm.modelKey) : undefined;
  const videoEstimateUsd = videoModel
    ? (videoModel.estCostUsdPerSec ? videoModel.estCostUsdPerSec * videoForm.durationSec : videoModel.estCostUsd)
    : null;
  const videoEstimate = videoEstimateUsd !== null
    ? `≈ $${videoEstimateUsd.toFixed(2)} · ${videoForm.durationSec} s ${videoForm.resolution}`
    : null;

  const start = () => {
    setError('');
    const spec = useFormSpec || !presetId ? readSpec() : null;
    if ((useFormSpec || !presetId) && !spec) {
      setError('Pick a model and fill in the prompt and negative prompt before testing.');
      return;
    }
    if (!file && !sourceUrl.trim()) {
      setError('Add a source image (JPG/PNG/WebP, max 6 MB) or paste an image URL.');
      return;
    }
    setStarting(true);
    EnterViceService.createTest({
      presetId,
      file,
      sourceImageUrl: file ? undefined : sourceUrl.trim(),
      ...(spec || {}),
    })
      .then((started) => {
        const run: TestRun = {
          testId: started.testId,
          status: started.status,
          kind: started.kind,
          presetId,
          presetKey: null,
          promptVersion: null,
          provider: started.provider,
          model: started.model,
          parentTestId: null,
          prompt: spec ? spec.prompt : '',
          negativePrompt: spec ? spec.negativePrompt : '',
          params: spec ? spec.params : null,
          sourceUrl: preview || sourceUrl.trim() || null,
          resultUrl: null,
          durationMs: null,
          cost: null,
          error: null,
          adminId: null,
          createdAt: new Date().toISOString(),
          startedAt: null,
          finishedAt: null,
        };
        setRuns((prev) => [run, ...prev]);
      })
      .catch((e) => setError(describeError(e, 'Could not start the test.').message))
      .finally(() => setStarting(false));
  };

  const animate = (run: TestRun) => {
    const spec = readVideoSpec();
    if (!spec) {
      setError('Enable video and choose a video model in the editor before animating a result.');
      return;
    }
    setError('');
    setAnimating(run.testId);
    EnterViceService.createVideoTest(run.testId, spec)
      .then((started) => {
        const videoRun: TestRun = {
          testId: started.testId,
          status: started.status,
          kind: 'video',
          presetId,
          presetKey: null,
          promptVersion: null,
          provider: started.provider,
          model: started.model,
          parentTestId: run.testId,
          motionPrompt: spec.motionPrompt,
          durationSec: spec.durationSec,
          resolution: spec.resolution,
          prompt: '',
          negativePrompt: '',
          params: null,
          sourceUrl: run.resultUrl,
          resultUrl: null,
          durationMs: null,
          cost: null,
          error: null,
          adminId: null,
          createdAt: new Date().toISOString(),
          startedAt: null,
          finishedAt: null,
        };
        setRuns((prev) => [videoRun, ...prev]);
      })
      .catch((e) => setError(describeError(e, 'Could not start the video test.').message))
      .finally(() => setAnimating(null));
  };

  const cancel = (run: TestRun) => {
    EnterViceService.cancelTest(run.testId)
      .then((result) => {
        setRuns((prev) => prev.map((r) => (r.testId === run.testId ? { ...r, status: result.status } : r)));
      })
      .catch((e) => setError(describeError(e, 'Could not cancel the test.').message));
  };

  const promote = (run: TestRun) => {
    if (!presetId) return;
    setPromoting(run.testId);
    setError('');
    EnterViceService.thumbnailFromTest(presetId, run.testId)
      .then((result) => {
        setPromotedId(run.testId);
        onThumbnailPromoted(result.thumbnailUrl);
      })
      .catch((e) => setError(describeError(e, 'Could not use this result as thumbnail.').message))
      .finally(() => setPromoting(null));
  };

  const elapsedSeconds = (run: TestRun) => Math.max(0, Math.round((Date.now() - new Date(run.createdAt).getTime()) / 1000));

  return (
    <div className="ev-card ev-testlab">
      <h3>Test Lab</h3>
      <p className="ev-hint">
        Run the prompt against a real source image before activating. A preset needs a thumbnail and one
        successful run to go live; with video enabled it also needs one successful video test — press
        “Animate” on a finished image result.
      </p>

      <div
        className={`ev-drop${dragging ? ' drag' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          applyFile(e.dataTransfer.files && e.dataTransfer.files.length > 0 ? e.dataTransfer.files[0] : null);
        }}
      >
        <input
          id="ev-test-source"
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          title=""
          onChange={(e) => applyFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null)}
        />
        {preview ? (
          <>
            <img src={preview} alt="source preview" />
            <small>{file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : ''}</small>
          </>
        ) : (
          <>
            <span>Drop a selfie or avatar render here, or click to choose</span>
            <small>JPG / PNG / WebP · max 6 MB · portrait works best</small>
          </>
        )}
      </div>
      {preview && (
        <div className="ev-run-controls">
          <span>Source image ready.</span>
          <button type="button" className="ev-mini" onClick={clearSource}>Remove image</button>
        </div>
      )}
      {!preview && (
        <div className="ev-source-url">
          <input
            id="ev-test-source-url"
            type="url"
            placeholder="…or paste a public image URL"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
          />
        </div>
      )}

      {presetId && (
        <label className="ev-toggle" htmlFor="ev-test-use-form" style={{ marginTop: 14 }}>
          <input
            id="ev-test-use-form"
            type="checkbox"
            checked={useFormSpec}
            onChange={(e) => setUseFormSpec(e.target.checked)}
          />
          Test with the current editor values (unsaved edits included)
        </label>
      )}

      {error && <div className="ev-error">{error}</div>}

      <div className="ev-run-controls">
        <span>{estimate || ''}</span>
        <Button buttonType="button" onClick={start} disabled={starting}>
          {starting ? 'Starting…' : 'Run test'}
        </Button>
      </div>

      {!presetId && (
        <div className="ev-note">Save the preset once to keep its test history and use a result as thumbnail.</div>
      )}

      {runs.length > 0 && (
        <div className="ev-runs">
          {runs.map((run) => {
            const active = isActive(run);
            const cost = formatCost(run.cost);
            const isVideo = run.kind === 'video';
            return (
              <div className="ev-run" key={run.testId}>
                <div className="ev-run-img">
                  {isVideo && run.resultUrl ? (
                    <video src={run.resultUrl} controls playsInline loop muted preload="metadata" />
                  ) : run.resultUrl ? (
                    <a href={run.resultUrl} target="_blank" rel="noreferrer">
                      <img src={run.resultUrl} alt="test result" />
                    </a>
                  ) : run.sourceUrl ? (
                    <img className="dim" src={run.sourceUrl} alt="source" />
                  ) : (
                    <span>{active ? '…' : 'no image'}</span>
                  )}
                </div>
                <div className="ev-run-meta">
                  <div className="ev-run-head">
                    <StatusChip status={run.status} />
                    <span className="mono">{run.model}</span>
                    {isVideo && (
                      <span className="mono">
                        {`video · ${run.durationSec ?? '?'} s${run.resolution ? ` · ${run.resolution}` : ''}`}
                      </span>
                    )}
                  </div>
                  {active && <div className="ev-progress"><span /></div>}
                  <span>
                    {active
                      ? `${elapsedSeconds(run)} s elapsed`
                      : run.durationMs !== null && run.durationMs !== undefined
                        ? `${(run.durationMs / 1000).toFixed(1)} s`
                        : ''}
                    {cost ? ` · ${cost}` : ''}
                  </span>
                  <span className="mono">
                    {getDateAndTime(run.createdAt)}
                    {run.promptVersion ? ` · prompt v${run.promptVersion}` : ''}
                  </span>
                  {run.error && (
                    <span className="ev-run-error">{run.error.code}: {run.error.message}</span>
                  )}
                  <div className="ev-run-actions">
                    {active && (
                      <button type="button" className="ev-mini" onClick={() => cancel(run)}>Cancel</button>
                    )}
                    {!isVideo && run.status === 'done' && run.resultUrl && presetId && canPromote && (
                      <button
                        type="button"
                        className="ev-mini ev-mini--primary"
                        disabled={promoting === run.testId || promotedId === run.testId}
                        onClick={() => promote(run)}
                      >
                        {promotedId === run.testId ? 'Thumbnail ✓' : promoting === run.testId ? 'Saving…' : 'Use as thumbnail'}
                      </button>
                    )}
                    {!isVideo && run.status === 'done' && run.resultUrl && videoForm.enabled && (
                      <button
                        type="button"
                        className="ev-mini ev-mini--primary"
                        disabled={animating === run.testId}
                        title={videoEstimate ? `Video test ${videoEstimate}` : 'Animate with the editor video settings'}
                        onClick={() => animate(run)}
                      >
                        {animating === run.testId ? 'Starting…' : `Animate${videoEstimate ? ` (${videoEstimate.split(' · ')[0]})` : ''}`}
                      </button>
                    )}
                    {run.status === 'done' && run.resultUrl && (
                      <a className="ev-mini" href={run.resultUrl} target="_blank" rel="noreferrer">Open</a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestLab;
